 import React from 'react';
import { connect } from 'react-redux';
import { fetchEventDetails, fetchEventDetailsLoading, updateEventLayout } from 'src/actions/event';
import { updateNoteworthyResponses } from 'src/actions/responses';
import { sessionCheck } from 'src/actions/user';
import { handleError, handleErrorWithDialog } from 'src/util/Errors';
import MainContent from 'src/components/creator-report/MainContent';
import ConcurrentSwitcher from 'src/components/creator-report/ConcurrentSwitcher';
import HeroRibbon from 'src/components/creator-report/HeroRibbon';
import EventDetailHeader from 'src/components/creator-report/EventDetailHeader';
import EventCollectionDetailHeader from 'src/components/creator-report/EventCollectionDetailHeader';
import DownloadPdfCsvDialog from 'src/components/creator-report/DownloadPdfCsvDialog';
import { TrackingAnalytics } from 'src/analytics/TrackingAnalytics';
import { showDialog } from 'src/actions/dialog';
import './CreatorReports.css';
import Logger from 'src/util/Logger';
import Event from 'src/models/Event';
import Collection from 'src/models/Collection';
import _ from 'lodash';
import {
  CREATOR_REPORTS_ALL_RESPONSES,
  CREATOR_REPORTS_COMMENT_BY_SENTIMENT,
  CREATOR_REPORTS_DISTRIBUTION,
  CREATOR_REPORTS_HISTORICAL_TREND,
  CREATOR_REPORTS_NOTEWORTHY,
  CREATOR_REPORTS_SEGMENTATION,
  CREATOR_REPORTS_SENTIMENT_BY_RATING,
  CREATOR_REPORTS_STATISTICS,
  CREATOR_REPORTS_TOP_RESPONSES,
  CREATOR_REPORTS_AI_INSIGHTS,
  GLOBAL_PATH_PREFIX
} from 'src/constants';
import { EventQuestionType } from 'src/models/EventQuestionType';
import Response from 'src/models/Response';
import Highcharts from 'highcharts';
import store from 'src/store/configureStore';
import { EventUseCase } from 'src/models/EventUseCase';
import { BrowserChecker } from 'src/BrowserChecker';
import { NavigationUtils } from 'src/util/NavigationUtils';
import { CreatorReportAnalytics } from 'src/analytics/CreatorReportAnalytics';
import { eventApi } from 'src/api/EventApi';
import { commentApi } from 'src/api/CommentApi';
import CsatModal from 'src/components/creator-report/CsatModal';
import { popTooltips } from 'src/services/pop-tooltips';
import Ideas from 'src/models/Ideas';

import queryString from 'stringquery';
import { popFileFetch } from 'src/services/pop-file-fetch';
import { ScreenRecorder } from 'src/util/ScreenRecorder';
import {CollectionSummary} from "src/components/creator-report/CollectionSummary";
import LoadingAndPulse from 'src/components/shared/LoadingAndPulse.jsx';

class CreatorReports extends React.Component {

  constructor (props) {
    super(props);
    TrackingAnalytics.mixpanelTrackPageViewed();

    // Mobile blocked from Creator Reports
    if (BrowserChecker.isMobile()) {
      const slug = props.match.params.event;
      const angularPath = !!slug ? `/q/${slug}/answers` : null;
      NavigationUtils.routeToLegacy(angularPath);
    }

    this.state = {
      event: null,
      editing: false,
      reset: false,
      redirectedFromAngular: false,
      showSummary: false,

      // Analytics
      reachedBottom: false,
      scrollPercentage: null,

      // PDF engine flags
      pageLoaded: false, // is the overall page loaded
      listWalked: false, // have page breaks been applied

      // noteworthy posts
      noteworthyPosts: this.getNoteworthyResponses(),

      // The hide layouts array is data sent over the network, it does not control any visual state
      sections: this.getSectionsWithAIInsights(props.layout),

      // These is flags do control visual state and should indicate the current state of the hide layouts array
      isNoteworthyResponsesEnabled: this.isNoteworthyResponsesEnabled(),
      isTopResponsesEnabled: this.isTopResponsesEnabled(),
      isStatsEnabled: this.isStatsEnabled(),
      isDistributionEnabled: this.isDistributionEnabled(),
      isHistoricalTrendEnabled: this.isHistoricalTrendEnabled(),
      isCommentsBySentimentEnabled: this.isCommentsBySentimentEnabled(),
      isSentimentByRatingEnabled: this.isSentimentByRatingEnabled(),
      isSegmentationEnabled: this.isSegmentationEnabled(),
      isAllResponsesEnabled: this.isAllResponsesEnabled(),
      isAIInsightsEnabled: this.isAIInsightsEnabled(),

      // Flag for the Download dialog
      showDownloadPdfCsvDialog: false,

      // Csat flags
      checkForCsatStatus: true,
      showCsatDialog: false,

      // PDF-only supplemental data
      pdfExtraContentKey: null,
      pdfExtraContentLoading: false,
      pdfExtraContentReady: true,
      pdfRatingDistributionByIdeaId: {},
      pdfCommentsByIdeaId: {}
    };
  }

  latestPdfExtraContentKey = null;

  isPdfRender = () => {
    const { location } = this.props;
    if (!location || !location.search) {
      return false;
    }

    const searchParams = queryString(location.search);
    return !!searchParams.tz;
  };

  getPdfIdeaIds = (ideas = this.props.ideas) => (
    (ideas || [])
      .map(idea => Ideas.getId(idea))
      .filter(id => !!id)
  );

  getPdfExtraContentKey = (event = this.props.event, ideas = this.props.ideas) => {
    if (!event) {
      return null;
    }

    const eventId = Event.getId(event);
    const ideaIds = this.getPdfIdeaIds(ideas);
    if (!eventId || ideaIds.length === 0) {
      return null;
    }

    return `${eventId}:${ideaIds.join(',')}`;
  };

  requiresPdfSupplementalData = (event = this.props.event, ideas = this.props.ideas) => (
    this.isPdfRender() &&
    !!event &&
    EventQuestionType.isMultipleRatingType(Event.getQuestionType(event)) &&
    this.getPdfIdeaIds(ideas).length > 0
  );

  hasPdfSupplementalDataFor = (event = this.props.event, ideas = this.props.ideas) => {
    if (!this.requiresPdfSupplementalData(event, ideas)) {
      return true;
    }

    const ideaIds = this.getPdfIdeaIds(ideas);
    if (ideaIds.length === 0) {
      return false;
    }

    return ideaIds.every((ideaId) => (
      Object.prototype.hasOwnProperty.call(this.state.pdfRatingDistributionByIdeaId, ideaId) &&
      Object.prototype.hasOwnProperty.call(this.state.pdfCommentsByIdeaId, ideaId)
    ));
  };

  primePdfSupplementalData = () => {
    const { event, ideas } = this.props;

    if (!this.requiresPdfSupplementalData(event, ideas)) {
      if (!this.state.pdfExtraContentReady ||
        this.state.pdfExtraContentLoading ||
        !!this.state.pdfExtraContentKey ||
        Object.keys(this.state.pdfRatingDistributionByIdeaId).length > 0 ||
        Object.keys(this.state.pdfCommentsByIdeaId).length > 0) {
        this.latestPdfExtraContentKey = null;
        this.setState({
          pdfExtraContentKey: null,
          pdfExtraContentLoading: false,
          pdfExtraContentReady: true,
          pdfRatingDistributionByIdeaId: {},
          pdfCommentsByIdeaId: {}
        });
      }
      return;
    }

    const eventId = Event.getId(event);
    const ideaIds = this.getPdfIdeaIds(ideas);
    const requestKey = this.getPdfExtraContentKey(event, ideas);
    if (!eventId || ideaIds.length === 0 || !requestKey) {
      if (this.state.pdfExtraContentReady) {
        this.setState({ pdfExtraContentReady: false });
      }
      return;
    }

    if (requestKey === this.state.pdfExtraContentKey && this.hasPdfSupplementalDataFor(event, ideas)) {
      if (!this.state.pdfExtraContentReady || this.state.pdfExtraContentLoading) {
        this.setState({
          pdfExtraContentLoading: false,
          pdfExtraContentReady: true
        });
      }
      return;
    }

    if (requestKey === this.state.pdfExtraContentKey && this.state.pdfExtraContentLoading) {
      return;
    }

    this.latestPdfExtraContentKey = requestKey;
    this.setState({
      pdfExtraContentKey: requestKey,
      pdfExtraContentLoading: true,
      pdfExtraContentReady: false,
      pdfRatingDistributionByIdeaId: {},
      pdfCommentsByIdeaId: {}
    });

    Promise.all(ideaIds.map((ideaId) => (
      Promise.all([
        eventApi.getRatingDistribution(eventId, ideaId)
          .then((distribution) => (Array.isArray(distribution) ? distribution : []))
          .catch((error) => {
            Logger.warn('Unable to prefetch rating distribution for PDF', ideaId, error);
            return [];
          }),
        commentApi.getComments(ideaId)
          .then((comments) => (Array.isArray(comments) ? comments : []))
          .catch((error) => {
            Logger.warn('Unable to prefetch comments for PDF', ideaId, error);
            return [];
          })
      ]).then(([distribution, comments]) => ({
        ideaId,
        distribution,
        comments
      }))
    ))).then((results) => {
      if (this.latestPdfExtraContentKey !== requestKey) {
        return;
      }

      const pdfRatingDistributionByIdeaId = {};
      const pdfCommentsByIdeaId = {};
      results.forEach(({ ideaId, distribution, comments }) => {
        pdfRatingDistributionByIdeaId[ideaId] = distribution;
        pdfCommentsByIdeaId[ideaId] = comments;
      });

      this.setState({
        pdfExtraContentLoading: false,
        pdfExtraContentReady: true,
        pdfRatingDistributionByIdeaId,
        pdfCommentsByIdeaId
      });
    }).catch((error) => {
      Logger.error('Unable to prefetch supplemental PDF content', error);
      if (this.latestPdfExtraContentKey !== requestKey) {
        return;
      }

      this.setState({
        pdfExtraContentLoading: false,
        pdfExtraContentReady: true
      });
    });
  };

  getSectionsWithAIInsights = (layout = []) => {
    const sections = _.cloneDeep(layout || []);
    const hasAIInsights = sections.some(section =>
      !!section && section.name === CREATOR_REPORTS_AI_INSIGHTS);

    if (this.isAIInsightsEnabled() && !hasAIInsights) {
      sections.push({
        name: CREATOR_REPORTS_AI_INSIGHTS,
        enabled: true
      });
    }

    return sections;
  };

  componentDidMount () {
    const { sessionCheck, location, history } = this.props;
    sessionCheck(false);

    // Scroll to page top on initial page load
    window.scrollTo(0, 0);

    if (window.matchMedia) {
      window.matchMedia('print').addListener(this.reflowCharts);
    }

    if (location && location.search) {
      const searchParams = queryString(location.search);

      // If we have the ?angular=1 search param, we are coming from the angular app
      if (!!searchParams.angular) {
        let searchString = null;
        for (let prop in searchParams) {
          // Remove the angular param from the URL
          if (prop !== 'angular') {
            if (!searchString) {
              searchString = `?${prop}=${searchParams[ prop ]}`;
            } else {
              searchString += `&${prop}=${searchParams[ prop ]}`;
            }
          }
        }

        // console.log(location);
        if (!!searchString) {
          history.replace(location.pathname, searchString);
        } else {
          history.replace(location.pathname);
        }
        this.setState({redirectedFromAngular: true});
      }

      // If you arrive here, it is because you came from the Angular app with flag set
      // meaning that the legacy Angular app force redirected the user here. Do special things?

      if (!!searchParams.summary) {
        this.setState({showSummary: true});
      }
    }
  }

  setScrollToBottomRef = ($element) => {
    const { event } = this.state;
    CreatorReportAnalytics.trackScrolledToBottom($element, Event.getId(event), {
      setScrollPercentage: (percentage) => {
        this.setState({ scrollPercentage: percentage });
      },
      hasReachedBottom: () => {
        return this.state.reachedBottom;
      },
      setReachedBottom: (reached) => {
        this.setState({ reachedBottom: reached });
      }
    });
  };

  componentDidUpdate (prevProps) {
    const { user, event, collection, noteworthy, collectionError, userError, layout, layoutError, segmentationError, location } = this.props;
    if (!prevProps.user && !!user) {
      ScreenRecorder.record(false);
    }
    handleErrorWithDialog(userError, prevProps.userError,
      'Unable to load your Account, please try again later');
    handleErrorWithDialog(collectionError, prevProps.collectionError,
      'Unable to load Collection');
    handleError(layoutError, prevProps.layoutError);
    handleError(segmentationError, prevProps.segmentationError);

    if (!!noteworthy) {
      if (!prevProps.noteworthy) {
        Logger.info('Sync noteworthy list first time');
        this.setState({ noteworthyPosts: this.getNoteworthyResponses() });
      } else if (!_.isEqual(noteworthy, prevProps.noteworthy)) {
        Logger.info('Update noteworthy list');
        this.setState({ noteworthyPosts: this.getNoteworthyResponses() });
      }
    }

    if (event && !prevProps.event && !this.state.event) {
      Logger.info('Initial event load: ', Event.getId(event));
      CreatorReportAnalytics.trackPageViewed(Event.getId(event));
      this.setState({ event });
    }

    if (!!event && !!Event.getRosterId(event) && !!prevProps.event &&
      !Event.getRosterId(prevProps.event)) {
      Logger.info('Event has been edited roster attached: ', Event.getId(event));
      this.setState({ event });
    }

    if (collection && !prevProps.collection && !this.state.collection) {
      Logger.info('Initial collection load: ', Collection.getId(collection));
      this.setState({ collection });
    }

    if (!this.state.editing) {
      if (layout && (!prevProps.layout || this.hasLayoutChanged(layout, prevProps.layout))) {
        Logger.info('Layout changed, update layout');
        this.updateLayout();
      }
    }

    this.primePdfSupplementalData();
    this.tellPdfEnginePageIsLoaded();

    // we want to block the CSAT modal from popping up if we are in the pdf-engines headless browser
    let okToShowCsat = true;
    if (!!location.search) {
      const searchParams = queryString(location.search);
      // the tz param is only present when we have generated a print url and sent that to the pdf-engine
      if (!!searchParams.tz) {
        okToShowCsat = false;
      }
    }
    if (okToShowCsat) {
      this.showCsatIfNeeded();
    }
  }

  showCsatIfNeeded = () => {
    const { checkForCsatStatus, event } = this.state;
    if (!!event && checkForCsatStatus && Event.isUserCreated(event)) {
      this.setState({ checkForCsatStatus: false }, () => {
        eventApi.getCsatStatus(Event.getId(event)).then(result => {
          if (result.should_show_csat && !window.matchMedia('print').matches) {
            Logger.info('Show CSAT dialog');
            this.setState({ showCsatDialog: true });
          } else {
            this.setState({ showCsatDialog: false });
          }
        }).catch(error => {
          // Failed to check csat status, don't check again until later
          handleError(error, null);
        });
      });
    }
  };

  tellPdfEnginePageIsLoaded = () => {
    let pageIsLoaded = false;
    const { event, responses, responsesLoading, responsesError, noteworthy, noteworthyError, stats, statsError, ideas, ideasError } = this.props;
    // We have an event
    if (!!event) {
      // We have responses - they may be blank but they are there, the responses are done loading and there was no error reported
      if (!!responses && responsesLoading === false && !responsesError) {
        // We have stats and no stats error
        if (!!stats && !statsError) {

          // We have noteworthy - they may be blank but they are there
          if (!!noteworthy && responsesLoading === false && !noteworthyError) {
            // We have ideas and no ideas error only in the case of needing ideas
            const needsIdeas = !EventQuestionType.isOpenEndedType(Event.getQuestionType(event));
            if (needsIdeas) {
              if (!!ideas && !ideasError) {
                pageIsLoaded = true;
              } else {
                Logger.warn('No ideas, page not done loading');
              }
            } else {
              pageIsLoaded = true;
            }
          } else {
            Logger.warn('No noteworthy, page not done loading');
          }
        } else {
          Logger.warn('No stats, page not done loading');
        }
      } else {
        Logger.warn('No responses, page not done loading');
      }
    } else {
      Logger.warn('No event, page not done loading');
    }

    if (pageIsLoaded && !this.hasPdfSupplementalDataFor(event, ideas)) {
      Logger.warn('Supplemental PDF content still loading, page not done loading');
      pageIsLoaded = false;
    }

    if (pageIsLoaded) {
      if (!this.state.pageLoaded) {
        // Give it just a bit more time
        setTimeout(() => this.setState({ pageLoaded: true }), this.isPdfRender() ? 1000 : 500);
      }
    }
  };

  updateLayout = () => {
    Logger.info('Update layout');
    this.setState({
      // The hide layouts array is data sent over the network, it does not control any visual state
      sections: this.getSectionsWithAIInsights(this.props.layout),

      // These is flags do control visual state and should indicate the current state of the hide layouts array
      isNoteworthyResponsesEnabled: this.isNoteworthyResponsesEnabled(),
      isTopResponsesEnabled: this.isTopResponsesEnabled(),
      isStatsEnabled: this.isStatsEnabled(),
      isDistributionEnabled: this.isDistributionEnabled(),
      isHistoricalTrendEnabled: this.isHistoricalTrendEnabled(),
      isCommentsBySentimentEnabled: this.isCommentsBySentimentEnabled(),
      isSentimentByRatingEnabled: this.isSentimentByRatingEnabled(),
      isSegmentationEnabled: this.isSegmentationEnabled(),
      isAllResponsesEnabled: this.isAllResponsesEnabled(),
      isAIInsightsEnabled: this.isAIInsightsEnabled()
    });
  };

  setComponentRef = ($element) => {
    TrackingAnalytics.listenForActiveUser($element, 'Event Details View');
  };

  hasLayoutChanged = (newLayout, oldLayout) => {
    const nextLayout = newLayout || [];
    const currentLayout = oldLayout || [];

    if (nextLayout.length !== currentLayout.length) {
      return true;
    }

    // Compare values with order
    for (let i = 0; i < nextLayout.length; ++i) {
      const o1 = nextLayout[ i ] || {};
      const o2 = currentLayout[ i ] || {};
      if (o1.name !== o2.name || o1.enabled !== o2.enabled) {
        return true;
      }
    }

    return false;
  };

  onEditModeClicked = () => {
    const editing = this.state.editing;
    // if editing then save the changes
    if (editing) {
      CreatorReportAnalytics.customizePublishedClicked(this);
      const reset = this.state.reset;
      // Leaving edit mode, update hide_layouts
      const { updateEventLayout, updateNoteworthyResponses } = this.props;
      const { event } = this.state;
      if (reset) {
        Logger.info('Full UI reset');

        // Reset the layout
        updateEventLayout(Event.getId(event), null);

        // Clear noteworthy responses
        updateNoteworthyResponses(Event.getId(event), []);
      } else {
        Logger.info('Publish UI changes');
        const { layout } = this.props;
        const { sections } = this.state;
        if (this.hasLayoutChanged(sections, layout)) {
          Logger.info('Update event layout');
          updateEventLayout(Event.getId(event), sections);
        }

        const { noteworthyPosts } = this.state;
        if (!_.isEqual(noteworthyPosts, this.getNoteworthyResponses())) {
          Logger.info('Update noteworthy responses', noteworthyPosts);
          updateNoteworthyResponses(Event.getId(event),
            noteworthyPosts.map(p => Response.getId(p)));
        }
      }
    } else {
      CreatorReportAnalytics.customizeClicked();
    }

    Logger.info('Sync state with props');
    this.setState({
      editing: !editing,

      // Clear reset flag
      reset: false,

      // The hide layouts array is data sent over the network, it does not control any visual state
      sections: this.getSectionsWithAIInsights(this.props.layout),

      // noteworthy posts
      noteworthyPosts: this.getNoteworthyResponses(),

      // These is flags do control visual state and should indicate the current state of the hide layouts array
      isNoteworthyResponsesEnabled: this.isNoteworthyResponsesEnabled(),
      isTopResponsesEnabled: this.isTopResponsesEnabled(),
      isStatsEnabled: this.isStatsEnabled(),
      isDistributionEnabled: this.isDistributionEnabled(),
      isHistoricalTrendEnabled: this.isHistoricalTrendEnabled(),
      isCommentsBySentimentEnabled: this.isCommentsBySentimentEnabled(),
      isSentimentByRatingEnabled: this.isSentimentByRatingEnabled(),
      isSegmentationEnabled: this.isSegmentationEnabled(),
      isAllResponsesEnabled: this.isAllResponsesEnabled(),
      isAIInsightsEnabled: this.isAIInsightsEnabled()
    }, () => {
      CreatorReportAnalytics.userDidEnterCustomize();
      this.reflowCharts();
      if (editing) {
        this.notifyReportUpdateSuccess();
      }
    });

  };

  componentWillUnmount () {
    // Whenever we umount, clear out the store of event details
    store.dispatch(fetchEventDetailsLoading());

    if (window.matchMedia) {
      window.matchMedia('print').removeListener(this.reflowCharts);
    }
  }

  reflowCharts = () => {
    // Get all highcharts on the page and reflow them
    Highcharts.charts.filter(chart => !!chart).forEach(chart => chart.reflow());
  };

  toggleDownloadPdfCsvDialog = () => {
    if (!this.state.showDownloadPdfCsvDialog) {
      CreatorReportAnalytics.exportReportModalOpened();
    }
    this.setState({ showDownloadPdfCsvDialog: !this.state.showDownloadPdfCsvDialog });
  };

  onHideCsatDialog = () => {
    this.setState({ showCsatDialog: false });
    // After csat modal closes, remind users how to get back to their recently closed POPin to edit or repop
    popTooltips.popinTabReminder().then((res) => {
      Logger.info(res);
    }).catch(error => {
      Logger.error('Error trying to show the Popin tab tooltip');
      handleError(error, null);
    });
  };

  handlePdfGeneration = (event, updateProgressBar, incrementCurrentPopin) => {
    let events = [ event ];
    if (this.state.collection) {
      events = Collection.getEvents(this.state.collection);
    }
    popFileFetch.zipPdfs(events, updateProgressBar,
      this.toggleDownloadPdfCsvDialog, incrementCurrentPopin);
  };

  navigateToNewEventInReact = (event) => {
    // Rewrite the URL so that page refreshing goes to the correct Event, and for visual consistency
    const replacePath = `${GLOBAL_PATH_PREFIX}/q/${Event.getSlug(event)}/answers`;
    window.history.replaceState(null, null, replacePath);

    // Scroll to page top on initial page load
    window.scrollTo(0, 0);

    // This will run synchronously and will guarantee that the loading has started before the setState is called
    // This will guarantee that information is cleared before fetching the next one.
    Logger.info('Clearing store first of event info');
    store.dispatch(fetchEventDetailsLoading());

    Logger.info('Changing state');
    // Closed event can stay here
    this.setState({ event, editing: false, checkForCsatStatus: true });
  };

  onChangeActiveEvent = (event) => {
    const currentEvent = this.state.event;
    if (currentEvent && Event.getId(currentEvent) !== Event.getId(event)) {
      Logger.info('Changing event:', Event.getId(event));
      this.navigateToNewEventInReact(event);
    } else {
      Logger.info('Same event, not changing:', Event.getId(event));
    }
    this.setState({showSummary: false})
  };

  onSummaryClick = (event) => {
    Logger.info('On summary clicked:', event);
    this.setState({showSummary: true})
  };

  doesHideLayoutsContain = (flag) => {
    const { sections } = this.state;
    if (!sections) {
      return false;
    } else {
      for (let section of sections) {
        if (section.name === flag) {
          return true;
        }
      }

      return false;
    }
  };

  addToHideLayouts = (flag) => {
    const sections = this.state.sections || [];
    for (let section of sections) {
      if (section.name === flag) {
        section.enabled = false;
        break;
      }
    }

    Logger.info('Hide section: ', flag, sections);
    this.setState({ sections: sections });
  };

  removeFromHideLayouts = (flag) => {
    const sections = this.state.sections || [];
    for (let section of sections) {
      if (section.name === flag) {
        section.enabled = true;
        break;
      }
    }

    Logger.info('Show section: ', flag, sections);
    this.setState({ sections: sections });
  };

  extractFlagFromLayout = (flag) => {
    const { layout } = this.props;
    // Pull from the `hide_sections` field on the response
    if (!layout || layout === null || layout === undefined) {
      // If the layout endpoint has not responded yet, fall back to default
      return true;
    } else {
      // If the layout contains the flag, hide it, else fall back to defaults
      for (let section of layout) {
        if (section.name === flag) {
          return section.enabled;
        }
      }

      // Hide it if we do not expect it
      return false;
    }
  };

  getNoteworthyResponses = () => {
    const { noteworthy } = this.props;
    const hasResponses = (noteworthy && noteworthy.length > 0);
    return hasResponses ? noteworthy : [];
  };

  getNoteworthyResponseIds = () => {
    return this.getNoteworthyResponses().map(r => Response.getId(r));
  };

  isNoteworthyResponsesEnabled = () => {
    if (!this.state) {
      return false;
    }
    return this.extractFlagFromLayout(CREATOR_REPORTS_NOTEWORTHY)
      && this.getNoteworthyResponseIds().length > 0;
  };

  isTopResponsesEnabled = () => {
    if (!this.state) {
      return false;
    }

    const { event } = this.state;
    if (!event) {
      return false;
    }

    const canCrowdsource = EventQuestionType.isOpenEndedType(Event.getQuestionType(event)) || (!!_.get(event, 'polls_crowdsourcing') && !EventQuestionType.isMultipleRatingType(Event.getQuestionType(event)));
    return this.extractFlagFromLayout(CREATOR_REPORTS_TOP_RESPONSES) && canCrowdsource;
  };

  isStatsEnabled = () => {
    if (!this.state) {
      return false;
    }
    return this.extractFlagFromLayout(CREATOR_REPORTS_STATISTICS);
  };

  isDistributionEnabled = () => {
    if (!this.state) {
      return false;
    }

    const { event } = this.state;
    return !!event &&
      !EventQuestionType.isOpenEndedType(Event.getQuestionType(event)) &&
      this.extractFlagFromLayout(CREATOR_REPORTS_DISTRIBUTION);
  };

  isHistoricalTrendEnabled = () => {
    if (!this.state) {
      return false;
    }

    const { pulseStats, pulseStatsError } = this.props;
    const { event } = this.state;

    if (!!event && EventUseCase.isBeforeQuestion(Event.getUseCase(event))) {
      // Historical trend is disabled for btm
      return false;
    } else {
      // Normal condition check
      // If we have stats and the list is not empty
      return ((!!pulseStats && pulseStats.length > 0) ||
        // Or we do not have pulse stats because of an error
        // Give them the opportunity to refetch the pulse stats
        (!pulseStats && !!pulseStatsError)) &&
        this.extractFlagFromLayout(CREATOR_REPORTS_HISTORICAL_TREND);
    }
  };

  isCommentsBySentimentEnabled = () => {
    if (!this.state) {
      return false;
    }

    const { event } = this.state;
    const { user } = this.props;
    const canCrowdsource = !!event && (EventQuestionType.isOpenEndedType(Event.getQuestionType(event)) || !!_.get(event, 'polls_crowdsourcing'));
    return (!!event && Event.isSentimentEnabled(event)) &&
      this.extractFlagFromLayout(CREATOR_REPORTS_COMMENT_BY_SENTIMENT) &&
      canCrowdsource &&
      !!_.get(user, 'plan.features.sentiment');
  };

  isSentimentByRatingEnabled = () => {
    if (!this.state) {
      return false;
    }

    const { user, sentimentByRating, sentimentByRatingError } = this.props;
    const { event } = this.state;
    return (!!event &&
      EventQuestionType.isScaleType(Event.getQuestionType(event)) &&
      Event.isSentimentEnabled(event)) &&
      // If we have sentiment and the list is not empty
      ((!!sentimentByRating && sentimentByRating.length > 0) ||
        // Or we do not have sentiment because of an error
        // Give them the opportunity to refetch the sentiment
        (!sentimentByRating && !!sentimentByRatingError)) &&
      this.extractFlagFromLayout(CREATOR_REPORTS_SENTIMENT_BY_RATING) &&
      !!_.get(user, 'plan.features.sentiment');
  };

  isSegmentationEnabled = () => {
    if (!this.state) {
      return false;
    }

    const { user } = this.props;
    const { event } = this.state;
    return (!!event &&
      !!Event.isUserCreated(event) &&
      !!Event.getRosterId(event)) &&
      Event.isSentimentEnabled(event) &&
      this.extractFlagFromLayout(CREATOR_REPORTS_SEGMENTATION) &&
      !!_.get(user, 'plan.features.segmentation');
  };

  isAllResponsesEnabled = () => {
    if (!this.state) {
      return false;
    }

    const { event } = this.state;
    if (!event) {
      return false;
    }

    const questionType = Event.getQuestionType(event);
    const canCrowdsource = EventQuestionType.isOpenEndedType(questionType) ||
      EventQuestionType.isStackRankType(questionType) ||
      (!!_.get(event, 'polls_crowdsourcing'));
    return this.extractFlagFromLayout(CREATOR_REPORTS_ALL_RESPONSES) && canCrowdsource;
  };

  // AI Insights is now available to all users. It will be shown whenever the
  // section is present in the report layout. Remove all per-user or per-path
  // gating logic.
  // AI Insights is universally enabled for all users and events.
  isAIInsightsEnabled = () => true;

  onStateChangedFromChild = (state, callback) => {
    Logger.info('Set state from child: ', state);
    this.setState(state, () => {
      if (!!callback) {
        callback(this);
      }
    });
  };

  exitEditMode = () => {
    CreatorReportAnalytics.customizeExitCancel();
    this.setState({
      editing: false,
      // Clear reset flag
      reset: false
    });
    this.updateState();
  };

  updateState = (showVisualNotification) => {
    Logger.info('Update state with props');
    this.setState({
      // The hide layouts array is data sent over the network, it does not control any visual state
      sections: this.getSectionsWithAIInsights(this.props.layout),

      // noteworthy posts
      noteworthyPosts: this.getNoteworthyResponses(),

      // These is flags do control visual state and should indicate the current state of the hide layouts array
      isNoteworthyResponsesEnabled: this.isNoteworthyResponsesEnabled(),
      isTopResponsesEnabled: this.isTopResponsesEnabled(),
      isStatsEnabled: this.isStatsEnabled(),
      isDistributionEnabled: this.isDistributionEnabled(),
      isHistoricalTrendEnabled: this.isHistoricalTrendEnabled(),
      isCommentsBySentimentEnabled: this.isCommentsBySentimentEnabled(),
      isSentimentByRatingEnabled: this.isSentimentByRatingEnabled(),
      isSegmentationEnabled: this.isSegmentationEnabled(),
      isAllResponsesEnabled: this.isAllResponsesEnabled(),
      isAIInsightsEnabled: this.isAIInsightsEnabled()
    });

    if (showVisualNotification) {
      this.notifyReportUpdateSuccess();
    }
  };

  notifyReportUpdateSuccess = () => {
    // Assume success I guess - even if it fails as soon as they publish it will be corrected.
    // Set small timeout or else the dialog is hidden by the call to the last dialog.
    // setTimeout(() => {
    //   Logger.info('Show success dialog');
    //   showDialog({
    //     title: 'Report updated successfully',
    //     body: <div />,
    //     ok: 'Ok'
    //   });
    // }, 1000);
  };

  onExitEditMode = ($event, force) => {
    if ($event) {
      $event.preventDefault();
    }

    const { showDialog } = this.props;
    const layoutChanged = !_.isEqual(this.state.sections, this.props.layout);
    const noteworthyChanged = !_.isEqual(this.state.noteworthyPosts, this.getNoteworthyResponses());

    CreatorReportAnalytics.customizeCancelClicked(this);
    if (!force && (layoutChanged || noteworthyChanged)) {
      showDialog({
        title: 'Unsaved changes',
        body: (
          <div>
            You have unsaved changes. Are you sure you want to continue without
            saving?
          </div>
        ),
        ok: 'Ok',
        onOk: () => {
          this.exitEditMode();
          CreatorReportAnalytics.customizeUnsavedChangedModal('ok');
        },
        cancel: 'Cancel',
        onCancel: () => {
          CreatorReportAnalytics.customizeUnsavedChangedModal('cancel');
        }
      });
    } else {
      // Exit the edit mode
      this.exitEditMode();
    }

  };

  disablePublishButton = () => {
    const layoutChanged = !_.isEqual(this.state.sections, this.props.layout);
    const noteworthyChanged = !_.isEqual(this.state.noteworthyPosts, this.getNoteworthyResponses());
    return !(layoutChanged || noteworthyChanged);
  };

  render () {
    const { location, user, responses, pulseStats, ideas, sentimentByRating, segmentation, loading, match, stats, roster } = this.props;
    const { event, collection, editing, noteworthyPosts, sections, showDownloadPdfCsvDialog, pageLoaded, listWalked, showCsatDialog, scrollPercentage, redirectedFromAngular, showSummary } = this.state;
    const isPdfRender = this.isPdfRender();
    const pdfSupplementalReady = this.hasPdfSupplementalDataFor(event, ideas);
    if (!user) {
      return (
        <LoadingAndPulse
          size={20}
          color="#3D9AE2"
          whatIsLoading="" />
      );
    } else {
      return (
        <div
          className={ `col creator-reports-${loading ? 'loading' : 'loaded'}
          creator-reports-${editing ? 'editing' : 'normal'}` }
          ref={ this.setComponentRef }>

          { /* PDF engine watches for this div when the page is done rendering. */ }
          { (!!pageLoaded && !!listWalked && !!pdfSupplementalReady) && <div id="all-content-ready" /> }

          <HeroRibbon
            event={ event }
            collection={ collection }
            editing={ editing }
            onEditModeClicked={ this.onEditModeClicked }
            redirectedFromAngular={redirectedFromAngular}
            onDownloadClicked={ this.toggleDownloadPdfCsvDialog }
 />

          <ConcurrentSwitcher
            event={ event }
            collection={ collection }
            editing={ editing }
            scrollPercentage={ scrollPercentage }
            showSummary={ showSummary }
            onSummaryClicked={ this.onSummaryClick }
            onActiveEventChanged={ this.onChangeActiveEvent } />

          <div className="breakout">
            { showSummary &&
              <EventCollectionDetailHeader
                collection={ collection } />
            }
            { !showSummary &&
              <EventDetailHeader
                event={ event }
                collection={ collection }
                editing={ editing }
                location={ location } />
            }
          </div>

          <div className="container py-3 report">
            <div className="content">
            { !showSummary ?
              <MainContent
                location={ location }
                event={ event }
                collection={ collection }
                responses={ responses }
                sections={ sections }
                match={ match }
                noteworthyPosts={ noteworthyPosts }
                editing={ editing }
                stats={ stats }
                ideas={ ideas }
                roster={ roster }
                pulseStats={ pulseStats }
                sentimentByRating={ sentimentByRating }
                segmentation={ segmentation }
                isPdfRender={ isPdfRender }
                pdfRatingDistributionByIdeaId={ this.state.pdfRatingDistributionByIdeaId }
                pdfCommentsByIdeaId={ this.state.pdfCommentsByIdeaId }
                disablePublish={ this.disablePublishButton() }
                onExitEditMode={ this.onExitEditMode }
                onEditModeClicked={ this.onEditModeClicked }
                onActiveEventChanged={ this.onChangeActiveEvent }
                doesHideLayoutsContain={ this.doesHideLayoutsContain }
                addToHideLayouts={ this.addToHideLayouts }
                removeFromHideLayouts={ this.removeFromHideLayouts }
                updateState={ this.updateState }
                onParentStateChanged={ this.onStateChangedFromChild }
                activeTab={ this.state.activeTab }
                isNoteworthyResponsesVisuallyEnabled={ this.state.isNoteworthyResponsesEnabled }
                isNoteworthyResponsesEnabled={ this.isNoteworthyResponsesEnabled() }
                isTopResponsesVisuallyEnabled={ this.state.isTopResponsesEnabled }
                isTopResponsesEnabled={ this.isTopResponsesEnabled() }
                isStatsVisuallyEnabled={ this.state.isStatsEnabled }
                isStatsEnabled={ this.isStatsEnabled() }
                isDistributionVisuallyEnabled={ this.state.isDistributionEnabled }
                isDistributionEnabled={ this.isDistributionEnabled() }
                isHistoricalTrendVisuallyEnabled={ this.state.isHistoricalTrendEnabled }
                isHistoricalTrendEnabled={ this.isHistoricalTrendEnabled() }
                isCommentsBySentimentVisuallyEnabled={ this.state.isCommentsBySentimentEnabled }
                isCommentsBySentimentEnabled={ this.isCommentsBySentimentEnabled() }
                isSentimentByRatingVisuallyEnabled={ this.state.isSentimentByRatingEnabled }
                isSentimentByRatingEnabled={ this.isSentimentByRatingEnabled() }
                isSegmentationVisuallyEnabled={ this.state.isSegmentationEnabled }
                isSegmentationEnabled={ this.isSegmentationEnabled() }
                isAllResponsesVisuallyEnabled={ this.state.isAllResponsesEnabled }
                isAllResponsesEnabled={ this.isAllResponsesEnabled() }
                isAIInsightsVisuallyEnabled={ this.state.isAIInsightsEnabled }
                isAIInsightsEnabled={ this.isAIInsightsEnabled() }
              />
             : <CollectionSummary
                    user={user}
                    collection={collection}
                    onActiveEventChanged={ this.onChangeActiveEvent }
                />}
            </div>
          </div>

          { showDownloadPdfCsvDialog && (
            <DownloadPdfCsvDialog
              toggleDialog={ this.toggleDownloadPdfCsvDialog }
              onReceivedNoteworthyPosts={ this.onReceivedNoteworthyPosts }
              handlePdfGeneration={ this.handlePdfGeneration }
              showOkButton={ this.showOkButton }
              event={ event }
              collection={ this.state.collection }
              noteworthyPosts={ noteworthyPosts }
              responses={ responses } />
          ) }

          { (showCsatDialog && false) && (
            <CsatModal
              onHidden={ this.onHideCsatDialog }
              event={ event }
            />
          ) }

          { !!event && (
            <div
              ref={ this.setScrollToBottomRef }
              id="scroll-to-bottom" style={ {
              minHeight: 1,
              minWidth: 1,
              maxHeight: 1,
              maxWidth: 1
            } } />
          ) }
        </div>
      );
    }
  }
}

// This ordering is only used as a fallback - the real displayed ordering is powered by the server.
export const getDefaultSections = (event) => {
  // TODO(peter): Wait for server. The client can handle two different orderings, but it is
  // overridden by the server returned endpoint info. We must wait for the server to return
  // different orderings from its endpoint before we can enable this.
  if (true && (!event || EventQuestionType.isOpenEndedType(Event.getQuestionType(event)))) {
    return [
      {
        name: CREATOR_REPORTS_NOTEWORTHY,
        enabled: false
      },
      {
        name: CREATOR_REPORTS_SEGMENTATION,
        enabled: false
      },
      {
        name: CREATOR_REPORTS_TOP_RESPONSES,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_STATISTICS,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_DISTRIBUTION,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_HISTORICAL_TREND,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_COMMENT_BY_SENTIMENT,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_SENTIMENT_BY_RATING,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_AI_INSIGHTS,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_ALL_RESPONSES,
        enabled: true
      }
    ];
  } else {
    return [
      {
        name: CREATOR_REPORTS_NOTEWORTHY,
        enabled: false
      },
      {
        name: CREATOR_REPORTS_SEGMENTATION,
        enabled: false
      },
      {
        name: CREATOR_REPORTS_DISTRIBUTION,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_STATISTICS,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_COMMENT_BY_SENTIMENT,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_SENTIMENT_BY_RATING,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_TOP_RESPONSES,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_HISTORICAL_TREND,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_AI_INSIGHTS,
        enabled: true
      },
      {
        name: CREATOR_REPORTS_ALL_RESPONSES,
        enabled: true
      }
    ];
  }
};

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    userError: state.user.error,
    event: state.event.event,
    collection: state.event.collection,
    collectionError: state.event.collectionError,
    loading: state.event.loading,
    layout: state.event.layout || getDefaultSections(state.event.event),
    layoutError: state.event.layoutError,
    responsesLoading: state.responses.loading,
    responses: state.responses.responses,
    responsesError: state.responses.error,
    noteworthy: state.responses.noteworthy,
    noteworthyError: state.responses.noteworthyError,
    ideas: state.event.ideas,
    ideasError: state.event.ideasError,
    stats: state.event.stats,
    statsError: state.event.statsError,
    roster: state.roster.roster,
    pulseStats: state.event.pulseStats,
    pulseStatsError: state.event.pulseStatsError,
    sentimentByRating: state.event.sentimentByRating,
    sentimentByRatingError: state.event.sentimentByRatingError,
    segmentation: state.event.roster,
    segmentationError: state.event.rosterError,
    pdfDownloadUrl: state.pdf.pdfDownloadUrl,
    pdfLoading: state.pdf.loading,
    pdfError: state.pdf.error
  };
};

export default connect(mapStateToProps, {
  fetchEventDetails,
  sessionCheck,
  showDialog,
  updateEventLayout,
  updateNoteworthyResponses
})(CreatorReports);
